// ============================================
// LIGA AJUPAM - Authentication Service
// ============================================
// DNI-based passwordless authentication
// @ts-nocheck - Supabase types use simplified `any` for flexibility

import { supabase, executeQuery, handleSupabaseError } from './client';
import { withTimeout } from '../../utils/withTimeout';
import type { Profile, UserSession, RegisterProfileInput, PlayerEntryOptions } from '../../types';
import { getTeamById } from './teams';
import { PROFILE_PUBLIC_COLUMNS, fetchUserPrivateProfiles, withPrivateFields } from './profilePrivacy';

const SESSION_KEY = 'liga_ajupam_session';
const SESSION_EXPIRY_DAYS = 7;
// v6.11.304 — Ligas visibles para los jugadores. Un equipo de una liga en
// borrador no se muestra ni es el equipo activo (vuelve cuando se publique).
const PUBLISHED_LEAGUE_STATUSES = ['enabled', 'archived'];

/**
 * Helper para llamar RPCs con manejo de errores
 */
const callRpc = async (functionName: string, params: any) => {
  const { data, error } = await supabase.rpc(functionName, params);
  if (error) throw handleSupabaseError(error);
  return data;
};

/**
 * Login con DNI (passwordless)
 * v6.11.213: lo resuelve el servidor (`user_login_with_dni`): busca el perfil,
 * crea la sesión y devuelve el perfil propio completo + el token. Antes el
 * navegador leía `profiles` por DNI y creaba el token él mismo
 * (`create_user_session`), lo que obligaba a dejar el DNI de todos legible.
 * v6.11.4 (Fase 9): `targetTeamId` permite ingresar a una edición distinta a la
 * actual (típicamente una finalizada, vía el hub multi-liga). La sesión queda en
 * modo solo-lectura cuando el equipo cargado pertenece a una edición finalizada.
 */
export const loginWithDNI = async (dni: number, targetTeamId?: string): Promise<UserSession> => {
  try {
    const login = await callRpc('user_login_with_dni', { p_dni: dni });

    // Verificar si se encontró el perfil
    if (!login?.found || !login.profile) {
      throw new Error('USER_NOT_FOUND');
    }

    const profile = login.profile;

    // v6.11.17.01: una persona puede ser admin Y participante de la liga. El
    // rol admin vive en el flujo aparte de /admin-liga (sesión admin propia); la
    // inscripción / experiencia de jugador va por la PWA como cualquier otro. Por
    // eso NO bloqueamos los perfiles is_admin acá — el DNI resuelve su sesión de
    // participante y, si además administra, ingresa al dashboard por separado.
    // (Antes, v6.8.3 cortaba todo perfil is_admin con ADMIN_ACCOUNT_USE_DASHBOARD,
    // lo que impedía anotarse a un admin que también juega.)

    // v6.11.17.03 — "Reemplazado permanentemente" ya NO bloquea el login.
    // El flag `was_replaced_permanently` nació en 2025 (era mono-edición) como un
    // bloqueo GLOBAL: al reemplazar a un jugador en su equipo, quedaba trabado de
    // TODO acceso. En multi-edición eso es incorrecto — un reemplazo en una edición
    // vieja no debe impedir inscribirse en una edición NUEVA (borrón y cuenta nueva).
    // Además, lanzar acá dejaba `user` en null: el flujo de inscripción tragaba el
    // error y, con `settings` null (multi-liga), el gate rebotaba al login por DNI
    // en loop infinito. Ahora dejamos que la sesión se resuelva y es la capa de
    // render la que decide: ReplacedPlayerScreen si ingresa sin equipo ni inscripción
    // en curso, o el flujo de Registration si eligió anotarse en una edición abierta.
    // El flag se limpia solo al (re)ingresar a un equipo (trigger en profiles).

    // Nota: Si el perfil está bloqueado, NO lanzamos error aquí.
    // La lógica de bloqueo se maneja a nivel de equipo en App.tsx
    // o en componentes específicos que usan team.is_blocked.

    // Si el usuario tiene un equipo, obtener detalles del equipo.
    // v6.11.4: targetTeamId permite cargar un equipo distinto al actual (otra edición).
    let team = null;
    let readOnly = false;
    let unpublishedLeague: string | undefined;
    const loadTeamId = targetTeamId || profile.current_team_id;
    if (loadTeamId) {
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select(`
          *,
          captain:captain_id(${PROFILE_PUBLIC_COLUMNS}),
          partner:partner_id(${PROFILE_PUBLIC_COLUMNS}),
          category:category_id(*)
        `)
        .eq('id', loadTeamId)
        .single();

      // Seguridad: si se pidió un targetTeamId que no pertenece al jugador, ignorarlo.
      const ownsTeam = !teamData || teamData.captain_id === profile.id || teamData.partner_id === profile.id;
      if (!teamError && teamData && ownsTeam) {
        team = teamData;

        // Verificar si el equipo está eliminado
        if (team.status === 'ELIMINADO') {
          throw new Error('TEAM_DELETED');
        }

        // Nota: Si el equipo está bloqueado, NO lanzamos error.
        // Permitimos el login y App.tsx mostrará RegularizeDebt

        // v6.10.0.29: adjuntar info de la liga del equipo + flag "edición finalizada"
        // (status='archived' o is_season_active=false) para que App.tsx pueda mostrar
        // la pantalla "tu edición finalizó" + puente a la nueva edición.
        if (team.league_id) {
          const { data: lg } = await supabase
            .from('leagues')
            .select('id, slug, name, status, edition_number, family_name')
            .eq('id', team.league_id)
            .maybeSingle();
          const { data: ls } = await supabase
            .from('league_settings')
            .select('is_season_active, is_registration_active')
            .eq('league_id', team.league_id)
            .maybeSingle();
          if (lg && !PUBLISHED_LEAGUE_STATUSES.includes(lg.status)) {
            // v6.11.304 — Liga no publicada: su equipo no se muestra.
            unpublishedLeague = lg.name;
            team = null;
          } else if (lg) {
            // v6.11.12 — "edición finalizada" == status 'archived' ÚNICAMENTE.
            // ANTES incluía `is_season_active === false`, que también es true en
            // PRE-temporada (inscripción abierta, temporada aún no arrancó) →
            // marcaba erróneamente como finalizada a una edición nueva y viva
            // (p.ej. la 5ta con inscripción abierta), poniéndola en solo-lectura.
            const editionFinished = lg.status === 'archived';
            // v6.11.4: solo-lectura solo cuando el jugador entró DELIBERADAMENTE a una
            // edición (targetTeamId, vía el hub o la opción única). Sin targetTeamId
            // (login directo / fallback) se conserva el flujo previo (SeasonEndedScreen).
            readOnly = editionFinished && !!targetTeamId;
            team.league = {
              ...lg,
              is_season_active: ls?.is_season_active ?? null,
              is_registration_active: ls?.is_registration_active ?? null,
              editionFinished,
            };
          }
        }
      }
    }

    // v6.11.304 — `current_team_id` apunta a un equipo de una liga no publicada:
    // si el jugador tiene otro equipo visible, se entra a ese (en curso primero).
    if (unpublishedLeague && !targetTeamId) {
      const opts = await getPlayerEntryOptions(dni).catch(() => null);
      const visible = opts?.leagues.find((l) => !l.is_finished) ?? opts?.leagues[0];
      if (visible) return loginWithDNI(dni, visible.team_id);
    }

    // v6.11.304 — La liga que el jugador elige (hub, Perfil, ?league=, opción
    // única) es la que disputa: se guarda en `profiles.current_team_id`, que es
    // lo que usan las RPC de partidos y el refresco de sesión. Antes la elección
    // quedaba sólo en el teléfono: aceptar/rechazar partidos y el botón de
    // actualizar seguían con el equipo anterior. Las finalizadas no se guardan
    // (se ven en solo lectura). Fail-soft: si falla, se entra igual.
    if (
      team && targetTeamId && team.league?.status === 'enabled' &&
      team.id !== profile.current_team_id
    ) {
      try {
        await callRpc('user_set_current_team', {
          p_session_token: login.session_token,
          p_team_id: team.id,
        });
        profile.current_team_id = team.id;
      } catch (e) {
        console.error('No se pudo guardar la liga elegida:', e);
      }
    }

    // Crear sesión (el token y el vencimiento los generó el servidor)
    const session: UserSession = {
      profile: profile as Profile,
      team: team || undefined,
      token: login.session_token,
      readOnly,
      viewingTeamId: team?.id,
      unpublishedLeague: team ? undefined : unpublishedLeague,
      expiresAt: login.expires_at ?? new Date(
        Date.now() + SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000
      ).toISOString(),
    };

    // Guardar sesión en localStorage
    saveSession(session);

    return session;
  } catch (error: any) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Registrar nuevo usuario (crear perfil)
 * Si el DNI existe pero el usuario no tiene equipo (jugador huérfano),
 * se recupera el perfil existente y se actualizan sus datos.
 */
export const registerProfile = async (data: RegisterProfileInput): Promise<Profile> => {
  try {
    // Llamar RPC para registrar perfil
    const result = await callRpc('user_register_profile', {
      p_dni: data.dni,
      p_full_name: data.full_name,
      p_phone: data.phone,
      p_instagram: data.instagram || null,
      p_photo_url: data.photo_url || null,
      p_category_validated: data.category_validated,
      p_birth_date: data.birth_date,
      p_position: data.position || null,
      p_dominant_hand: data.dominant_hand || null,
      p_nickname: data.nickname || null,
      p_sex: data.sex || null,
      p_shirt_size: data.shirt_size || null,
      p_shirt_style: data.sex === 'femenino' ? (data.shirt_style || null) : null,
      p_city: data.city || null,
      p_province: data.province || null,
      p_country: data.country || null
    });

    // La RPC con RETURNS TABLE retorna un array, no un objeto
    // Necesitamos acceder al primer elemento del array
    const profileData = Array.isArray(result) ? result[0] : result;
    console.log('[DEBUG] profileData:', profileData);
    console.log('[DEBUG] profileData.profile_id:', profileData?.profile_id);

    if (!profileData || !profileData.profile_id) {
      throw new Error('La RPC no retornó un profile_id válido');
    }

    // Obtener el perfil de la base de datos. v6.11.213: sólo las columnas
    // públicas; DNI, teléfono y nacimiento son los que la persona acaba de cargar
    // (la RPC los guardó tal cual; el teléfono puede quedar normalizado en la
    // base, y el próximo ingreso trae el valor guardado).
    const { data: publicProfile, error } = await supabase
      .from('profiles')
      .select(PROFILE_PUBLIC_COLUMNS)
      .eq('id', profileData.profile_id)
      .single();

    if (error || !publicProfile) {
      console.error('[DEBUG] Error fetching full profile:', error);
      throw new Error('Error al obtener perfil después de registro');
    }

    const fullProfile = {
      ...publicProfile,
      dni: profileData.profile_dni ?? data.dni,
      phone: data.phone,
      birth_date: data.birth_date,
    };
    console.log('[DEBUG] Full profile fetched successfully:', fullProfile);
    return fullProfile as Profile;
  } catch (error: any) {
    console.error('Register error:', error);
    throw error;
  }
};

/**
 * v6.11.4 (Fase 9) — Opciones de ingreso por DNI para el hub multi-liga.
 * Devuelve las ediciones donde el jugador tiene equipo + inscripciones abiertas.
 */
export const getPlayerEntryOptions = async (dni: number): Promise<PlayerEntryOptions> => {
  // Timeout: alimenta el ingreso por DNI ("CONTINUAR"). Sin esto, un RPC colgado
  // dejaba el login trabado tras tocar el botón.
  const { data, error } = await withTimeout(
    (supabase.rpc as any)('get_player_entry_options', { p_dni: dni }),
    15000,
    'get_player_entry_options',
  );
  if (error) throw handleSupabaseError(error);
  return data as PlayerEntryOptions;
};

/**
 * Logout
 * Elimina la sesión del localStorage
 */
export const logout = (): void => {
  localStorage.removeItem(SESSION_KEY);
};

/**
 * Obtener sesión actual del localStorage
 */
export const getCurrentSession = (): UserSession | null => {
  try {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;

    const session: UserSession = JSON.parse(sessionStr);

    // Verificar si la sesión expiró
    if (session.expiresAt && new Date(session.expiresAt) < new Date()) {
      logout();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error reading session:', error);
    return null;
  }
};

/**
 * Guardar sesión en localStorage
 */
export const saveSession = (session: UserSession): void => {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Error saving session:', error);
  }
};

/**
 * Actualizar sesión (útil después de cambios en perfil o equipo)
 */
export const refreshSession = async (logoutOnError: boolean = true): Promise<UserSession | null> => {
  let currentSession = getCurrentSession();
  if (!currentSession) return null;

  try {
    // v6.11.218: una sesión guardada puede haber quedado SIN el DNI (y sin
    // teléfono/nacimiento). La versión anterior a v6.11.213 reemplazaba el perfil
    // entero con lo que llegaba por Realtime, y desde que la base no manda esas
    // columnas, el perfil llegaba sin ellas. Sin DNI no hay re-ingreso: se
    // recuperan con el token de la sesión (el servidor le da al jugador sus
    // propios datos) antes de seguir.
    if (currentSession.profile?.id && !currentSession.profile.dni && currentSession.token) {
      const own = (await fetchUserPrivateProfiles(currentSession.token, [currentSession.profile.id]))
        .get(currentSession.profile.id);
      if (own?.dni) {
        currentSession = { ...currentSession, profile: { ...currentSession.profile, ...own } };
        saveSession(currentSession);
      }
    }

    // v6.11.4: si estamos viendo una edición en modo solo-lectura, re-loguear
    // scopeado a ese equipo (no a current_team_id) para no perder el contexto.
    const targetTeamId = currentSession.readOnly ? currentSession.viewingTeamId : undefined;
    // Timeout: refreshSession alimenta el arranque (AuthProvider.loadSession) y el
    // refresco tras cambios de equipo. loginWithDNI encadena varias queries sin
    // acotar; si una queda colgada, el `.then` que setea user/team nunca corre y la
    // app se queda con los datos viejos (o sin equipo) para siempre.
    const newSession = await withTimeout(
      loginWithDNI(currentSession.profile.dni, targetTeamId),
      20000,
      'refreshSession/loginWithDNI',
    );

    // En modo normal, refrescar los detalles más recientes del equipo actual.
    // En modo lectura, loginWithDNI ya cargó el equipo elegido (con su liga).
    // v6.11.304: el equipo que cargó loginWithDNI (no `current_team_id` a secas):
    // si éste es de una liga no publicada, loginWithDNI lo saltea y no hay que
    // volver a ponerlo acá.
    if (!currentSession.readOnly && newSession?.team?.id) {
      const teamData = await withTimeout(
        getTeamById(newSession.team.id),
        15000,
        'refreshSession/getTeamById',
      );
      if (teamData) {
        // `getTeamById` selecciona `teams.*` + captain/partner/category, pero NO
        // trae el objeto `league` que loginWithDNI adjunta (slug, name, status,
        // editionFinished, is_season_active). Pisar el team sin re-adjuntarlo dejaba
        // `team.league` en undefined después de CUALQUIER refresco, y de ahí:
        //   - SeasonEndedScreen (gate `team.league?.editionFinished`) dejaba de
        //     aparecerle al jugador de una edición terminada;
        //   - el banner de solo-lectura mostraba "una edición finalizada" en vez del
        //     nombre de la liga;
        //   - `needsDeepLinkReconcile` (compara `team.league?.slug` con `?league=`)
        //     daba siempre true → spinner + re-login al abrir cualquier link de liga.
        // Sólo se reusa si es el MISMO equipo (acá siempre lo es: se pide a
        // getTeamById el mismo equipo que cargó loginWithDNI).
        const prevTeam = newSession.team;
        const prevLeague = prevTeam?.id === teamData.id ? prevTeam.league : undefined;
        newSession.team = prevLeague ? { ...teamData, league: prevLeague } : teamData;
        saveSession(newSession); // Guardar la sesión actualizada con el equipo fresco
      }
    }
    return newSession;
  } catch (error) {
    console.error('Error refreshing session:', error);
    // v6.11.12 — el refresh de arranque (logoutOnError=false) NO debe desloguear
    // ante un error de red transitorio; se conserva la sesión guardada.
    if (logoutOnError) logout();
    return null;
  }
};

/**
 * Verificar si el usuario actual es admin
 */
export const isAdmin = (): boolean => {
  const session = getCurrentSession();
  return session?.profile?.is_admin || false;
};

/**
 * Obtener perfil de usuario por ID
 * v6.11.213: columnas públicas + los datos privados que le corresponden al
 * jugador con sesión (a su compañero/a: teléfono y nacimiento). Se usa para la
 * ficha de la propia pareja (panel, vista de equipo, pre-temporada).
 */
export const getUserById = async (userId: string): Promise<Profile> => {
  try {
    const [{ data, error }, privateMap] = await Promise.all([
      supabase
        .from('profiles')
        .select(PROFILE_PUBLIC_COLUMNS)
        .eq('id', userId),
      fetchUserPrivateProfiles(getCurrentSession()?.token, [userId]),
    ]);

    // Manejar caso donde no hay datos (perfil no existe)
    if (error) {
      // Error 406 "PGRST116" significa que no se encontró el registro
      if (error.code === 'PGRST116' || error.message.includes('coerce')) {
        throw new Error(`USER_NOT_FOUND: Profile with ID ${userId} does not exist`);
      }
      throw handleSupabaseError(error);
    }

    if (!data || data.length === 0) {
      throw new Error(`USER_NOT_FOUND: Profile with ID ${userId} does not exist`);
    }

    // Si hay múltiples resultados (no debería pasar), tomar el primero
    return withPrivateFields(data[0], privateMap) as Profile;
  } catch (error: any) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Reactivar equipo eliminado
 * Cambia el status del equipo y actualiza la registration_date
 */
export const reactivateTeam = async (teamId: string): Promise<void> => {
  try {
    const currentSession = getCurrentSession();
    if (!currentSession?.token) {
      throw new Error('No hay sesión activa');
    }

    // Llamar RPC para reactivar equipo
    await callRpc('user_reactivate_team', {
      p_session_token: currentSession.token,
      p_team_id: teamId
    });

    // Refrescar sesión
    await refreshSession();
  } catch (error: any) {
    console.error('Error reactivating team:', error);
    throw error;
  }
};
